import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  TextField,
  Button,
  Box,
  Chip,
  Stack,
  Avatar,
  LinearProgress,
  Alert,
  Modal,
  IconButton,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Iconify from '../components/iconify';
import api from '../services/api';

// ----------------------------------------------------------------------

const typeColors = {
  bug: '#92bc2c',
  dark: '#595761',
  dragon: '#0c69c8',
  electric: '#f2d94e',
  fairy: '#e91e63',
  fighting: '#d3425f',
  fire: '#fba54c',
  flying: '#a1bbec',
  ghost: '#5f6dbc',
  grass: '#5fbd58',
  ground: '#da7c4d',
  ice: '#75d0c1',
  normal: '#a0a29f',
  poison: '#b763cf',
  psychic: '#fa8581',
  rock: '#c9bb8a',
  steel: '#5695a3',
  water: '#539ddf',
};

// Type effectiveness chart
const typeEffectiveness = {
  normal: { weak: ['fighting'], resistant: [], immune: ['ghost'] },
  fire: { weak: ['water', 'ground', 'rock'], resistant: ['fire', 'grass', 'ice', 'bug', 'steel', 'fairy'], immune: [] },
  water: { weak: ['electric', 'grass'], resistant: ['fire', 'water', 'ice', 'steel'], immune: [] },
  electric: { weak: ['ground'], resistant: ['electric', 'flying', 'steel'], immune: [] },
  grass: { weak: ['fire', 'ice', 'poison', 'flying', 'bug'], resistant: ['water', 'electric', 'grass', 'ground'], immune: [] },
  ice: { weak: ['fire', 'fighting', 'rock', 'steel'], resistant: ['ice'], immune: [] },
  fighting: { weak: ['flying', 'psychic', 'fairy'], resistant: ['bug', 'rock', 'dark'], immune: [] },
  poison: { weak: ['ground', 'psychic'], resistant: ['grass', 'fighting', 'poison', 'bug', 'fairy'], immune: [] },
  ground: { weak: ['water', 'grass', 'ice'], resistant: ['poison', 'rock'], immune: ['electric'] },
  flying: { weak: ['electric', 'ice', 'rock'], resistant: ['grass', 'fighting', 'bug'], immune: ['ground'] },
  psychic: { weak: ['bug', 'ghost', 'dark'], resistant: ['fighting', 'psychic'], immune: [] },
  bug: { weak: ['fire', 'flying', 'rock'], resistant: ['grass', 'fighting', 'ground'], immune: [] },
  rock: { weak: ['water', 'grass', 'fighting', 'ground', 'steel'], resistant: ['normal', 'fire', 'poison', 'flying'], immune: [] },
  ghost: { weak: ['ghost', 'dark'], resistant: ['poison', 'bug'], immune: ['normal', 'fighting'] },
  dragon: { weak: ['ice', 'dragon', 'fairy'], resistant: ['fire', 'water', 'electric', 'grass'], immune: [] },
  dark: { weak: ['fighting', 'bug', 'fairy'], resistant: ['ghost', 'dark'], immune: ['psychic'] },
  steel: { weak: ['fire', 'fighting', 'ground'], resistant: ['normal', 'grass', 'ice', 'flying', 'psychic', 'bug', 'rock', 'dragon', 'steel', 'fairy'], immune: ['poison'] },
  fairy: { weak: ['poison', 'steel'], resistant: ['fighting', 'bug', 'dark'], immune: ['dragon'] },
};

const StyledCounterCard = styled(Card)(({ theme, score }) => ({
  height: '100%',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: `2px solid ${score >= 4 ? '#00C853' : score >= 3 ? '#FFB300' : '#E0E0E0'}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

// ----------------------------------------------------------------------

export default function CountersFinderPage() {
  const [pokemonList, setPokemonList] = useState([]);
  const [selectedPokemon, setSelectedPokemon] = useState(null);
  const [pokemonDetails, setPokemonDetails] = useState(null);
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  useEffect(() => {
    loadPokemonList();
  }, []);

  const loadPokemonList = async () => {
    setLoading(true);
    try {
      // Carregar lista básica de Pokémon para o autocomplete
      const promises = [];
      for (let i = 1; i <= 905; i += 1) {
        promises.push(
          api.get(`/pokemon/${i}`).then(res => ({
            id: res.data.id,
            name: res.data.name,
            sprite: res.data.sprites.front_default,
            types: res.data.types,
            stats: res.data.stats,
          }))
        );
      }
      const allPokemon = await Promise.all(promises);
      setPokemonList(allPokemon);
    } catch (error) {
      // Error loading Pokemon
    } finally {
      setLoading(false);
    }
  };

  const calculateStatScore = (stats) => {
    // Calcular força do Pokémon baseado nos stats
    // Peso maior para Attack e Speed (importantes para counters)
    const hp = stats.find(s => s.stat.name === 'hp')?.base_stat || 0;
    const attack = stats.find(s => s.stat.name === 'attack')?.base_stat || 0;
    const defense = stats.find(s => s.stat.name === 'defense')?.base_stat || 0;
    const spAttack = stats.find(s => s.stat.name === 'special-attack')?.base_stat || 0;
    const spDefense = stats.find(s => s.stat.name === 'special-defense')?.base_stat || 0;
    const speed = stats.find(s => s.stat.name === 'speed')?.base_stat || 0;
    
    // Fórmula balanceada: prioriza ataque e velocidade
    const statScore = (
      (hp * 0.5) +
      (attack * 1.5) +
      (defense * 0.8) +
      (spAttack * 1.5) +
      (spDefense * 0.8) +
      (speed * 1.2)
    ) / 100; // Normalizar para escala similar ao type score
    
    return statScore;
  };

  const calculateCounterScore = (attackerTypes, attackerStats, defenderTypes) => {
    let typeScore = 0;
    
    // Para cada tipo do atacante
    attackerTypes.forEach(attackerType => {
      const atkType = attackerType.type.name;
      
      // Verificar contra cada tipo do defensor
      defenderTypes.forEach(defenderType => {
        const defType = defenderType.type.name;
        const defTypeData = typeEffectiveness[defType];
        
        if (!defTypeData) return;
        
        // Super efetivo (atacante é fraqueza do defensor)
        if (defTypeData.weak.includes(atkType)) {
          typeScore += 2;
        }
        // Não muito efetivo (defensor resiste)
        else if (defTypeData.resistant.includes(atkType)) {
          typeScore -= 1;
        }
        // Imune (defensor é imune)
        else if (defTypeData.immune.includes(atkType)) {
          typeScore -= 2;
        }
      });
    });
    
    // Calcular score dos stats
    const statScore = calculateStatScore(attackerStats);
    
    // Score final: type advantage (70%) + stats (30%)
    const finalScore = (typeScore * 0.7) + (statScore * 0.3);
    
    return parseFloat(finalScore.toFixed(2));
  };

  const findCounters = async () => {
    if (!selectedPokemon) return;
    
    setAnalyzing(true);
    try {
      // Buscar detalhes completos do Pokémon selecionado
      const response = await api.get(`/pokemon/${selectedPokemon.id}`);
      setPokemonDetails(response.data);
      
      const targetTypes = response.data.types;
      
      // Calcular score para cada Pokémon
      const counterScores = pokemonList
        .filter(p => p.id !== selectedPokemon.id) // Não incluir o próprio Pokémon
        .map(pokemon => ({
          ...pokemon,
          score: calculateCounterScore(pokemon.types, pokemon.stats, targetTypes),
        }))
        .filter(p => p.score > 0) // Apenas Pokémon com vantagem
        .sort((a, b) => b.score - a.score) // Ordenar por melhor score
        .slice(0, 12); // Top 12 counters
      
      setCounters(counterScores);
    } catch (error) {
      // Error analyzing
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreLabel = (score) => {
    if (score >= 3) return { text: 'Excellent Counter', color: 'success' };
    if (score >= 2) return { text: 'Good Counter', color: 'warning' };
    if (score >= 1) return { text: 'Decent Counter', color: 'info' };
    return { text: 'Weak Counter', color: 'default' };
  };

  return (
    <>
      <Helmet>
        <title>Counters Finder | Pokémon Dashboard</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h4">
              ⚔️ Counters Finder
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Find the best Pokémon to counter your opponent based on type advantages
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:info-outline" />}
            onClick={() => setInfoModalOpen(true)}
            sx={{ flexShrink: 0, ml: 2 }}
          >
            How it works
          </Button>
        </Stack>
        <Box sx={{ mb: 4 }} />

        {/* Seletor de Pokémon */}
        <Card sx={{ mb: 4, p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={pokemonList}
                getOptionLabel={(option) => `#${option.id} ${option.name.charAt(0).toUpperCase() + option.name.slice(1)}`}
                loading={loading}
                value={selectedPokemon}
                onChange={(event, newValue) => setSelectedPokemon(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select a Pokémon to counter"
                    placeholder="Search by name or number..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <Iconify icon="eva:search-fill" sx={{ ml: 1, mr: 1, color: 'text.disabled' }} />
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar src={option.sprite} sx={{ width: 40, height: 40 }} />
                    <Box>
                      <Typography variant="body2">
                        #{option.id.toString().padStart(3, '0')} {option.name.charAt(0).toUpperCase() + option.name.slice(1)}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {option.types.map(t => (
                          <Chip
                            key={t.type.name}
                            label={t.type.name}
                            size="small"
                            sx={{
                              bgcolor: typeColors[t.type.name],
                              color: 'white',
                              fontSize: '0.65rem',
                              height: 18,
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={findCounters}
                disabled={!selectedPokemon || analyzing}
                startIcon={<Iconify icon="eva:flash-fill" />}
                sx={{ height: 56 }}
              >
                {analyzing ? 'Analyzing...' : 'Find Counters'}
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* Pokémon Selecionado */}
        {pokemonDetails && (
          <Card sx={{ mb: 4, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Selected Target
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar
                  src={pokemonDetails.sprites.front_default}
                  sx={{ width: 80, height: 80, border: '3px solid', borderColor: 'divider' }}
                />
              </Grid>
              <Grid item xs>
                <Typography variant="h5">
                  #{pokemonDetails.id.toString().padStart(3, '0')} {pokemonDetails.name.charAt(0).toUpperCase() + pokemonDetails.name.slice(1)}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {pokemonDetails.types.map(t => (
                    <Chip
                      key={t.type.name}
                      label={t.type.name.toUpperCase()}
                      sx={{
                        bgcolor: typeColors[t.type.name],
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Weak to:</strong>{' '}
                  {pokemonDetails.types.flatMap(t => typeEffectiveness[t.type.name]?.weak || [])
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .join(', ')}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        )}

        {/* Loading */}
        {analyzing && (
          <Box sx={{ mb: 4 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
              Analyzing type matchups...
            </Typography>
          </Box>
        )}

        {/* Resultados */}
        {counters.length > 0 && (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              Found {counters.length} effective counters! Higher score means better type advantage.
            </Alert>

            <Grid container spacing={3}>
              {counters.map((counter) => {
                const scoreInfo = getScoreLabel(counter.score);
                return (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={counter.id}>
                    <StyledCounterCard score={counter.score}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar
                            src={counter.sprite}
                            sx={{
                              width: 80,
                              height: 80,
                              mx: 'auto',
                              mb: 1,
                              bgcolor: 'grey.100',
                            }}
                          />
                          <Chip
                            label={`Score: ${counter.score}`}
                            color={scoreInfo.color}
                            size="small"
                            sx={{ mb: 1, fontWeight: 600 }}
                          />
                          <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 600 }}>
                            #{counter.id.toString().padStart(3, '0')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {counter.name.charAt(0).toUpperCase() + counter.name.slice(1)}
                          </Typography>
                          <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" gap={0.5} sx={{ mb: 0.5 }}>
                            {counter.types.map(t => (
                              <Chip
                                key={t.type.name}
                                label={t.type.name}
                                size="small"
                                sx={{
                                  bgcolor: typeColors[t.type.name],
                                  color: 'white',
                                  fontSize: '0.65rem',
                                  height: 20,
                                }}
                              />
                            ))}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            ATK: {counter.stats.find(s => s.stat.name === 'attack')?.base_stat} | 
                            SPD: {counter.stats.find(s => s.stat.name === 'speed')?.base_stat}
                          </Typography>
                        </Box>
                      </CardContent>
                    </StyledCounterCard>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {/* Empty State */}
        {!analyzing && counters.length === 0 && selectedPokemon && pokemonDetails && (
          <Alert severity="info">
            Select a Pokémon and click "Find Counters" to see the best matchups!
          </Alert>
        )}

        {/* Info Modal */}
        <Modal
          open={infoModalOpen}
          onClose={() => setInfoModalOpen(false)}
          aria-labelledby="info-modal-title"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '90%', sm: '80%', md: 600 },
              maxHeight: '90vh',
              overflow: 'auto',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Typography id="info-modal-title" variant="h5" component="h2">
                📊 How Counter Score is Calculated
              </Typography>
              <IconButton onClick={() => setInfoModalOpen(false)} size="small">
                <Iconify icon="eva:close-outline" />
              </IconButton>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Type Advantage */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify icon="mdi:shield-sword" width={24} />
                Type Advantage (70% weight)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                The most important factor. We analyze type matchups between the counter and target Pokémon:
              </Typography>
              <Stack spacing={1} sx={{ pl: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="+2" color="success" size="small" sx={{ fontWeight: 600, minWidth: 40 }} />
                  <Typography variant="body2">
                    <strong>Super Effective:</strong> Counter's type attacks target's weakness
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="0" color="default" size="small" sx={{ fontWeight: 600, minWidth: 40 }} />
                  <Typography variant="body2">
                    <strong>Neutral:</strong> Normal damage effectiveness
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="-1" color="warning" size="small" sx={{ fontWeight: 600, minWidth: 40 }} />
                  <Typography variant="body2">
                    <strong>Not Very Effective:</strong> Target resists counter's type
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="-2" color="error" size="small" sx={{ fontWeight: 600, minWidth: 40 }} />
                  <Typography variant="body2">
                    <strong>No Effect:</strong> Target is immune to counter's type
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Base Stats */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify icon="mdi:chart-line" width={24} />
                Base Stats (30% weight)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Overall strength of the Pokémon based on base stats:
              </Typography>
              <Stack spacing={0.5} sx={{ pl: 2 }}>
                <Typography variant="body2">
                  <strong>Attack:</strong> 1.5× multiplier (highest priority)
                </Typography>
                <Typography variant="body2">
                  <strong>Special Attack:</strong> 1.5× multiplier (highest priority)
                </Typography>
                <Typography variant="body2">
                  <strong>Speed:</strong> 1.2× multiplier (important for going first)
                </Typography>
                <Typography variant="body2">
                  <strong>Defense:</strong> 0.8× multiplier
                </Typography>
                <Typography variant="body2">
                  <strong>Special Defense:</strong> 0.8× multiplier
                </Typography>
                <Typography variant="body2">
                  <strong>HP:</strong> 0.5× multiplier (lowest priority)
                </Typography>
              </Stack>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Final Formula */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify icon="mdi:calculator" width={24} />
                Final Score Formula
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Score = (Type Advantage × 0.7) + (Stats Score × 0.3)</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Normalized to a 0-5 scale, where higher is better
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Score Interpretation */}
            <Box>
              <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify icon="mdi:medal" width={24} />
                Score Interpretation
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="3.0+" color="success" size="small" sx={{ fontWeight: 600, minWidth: 60 }} />
                  <Typography variant="body2">
                    <strong>Excellent Counter</strong> - Strong type advantage + great stats
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="2.0+" color="warning" size="small" sx={{ fontWeight: 600, minWidth: 60 }} />
                  <Typography variant="body2">
                    <strong>Good Counter</strong> - Good matchup overall
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="1.0+" color="info" size="small" sx={{ fontWeight: 600, minWidth: 60 }} />
                  <Typography variant="body2">
                    <strong>Decent Counter</strong> - Some advantage present
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ mt: 4, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
              <Typography variant="body2" color="info.darker">
                <strong>💡 Tip:</strong> The system prioritizes offensive capabilities (Attack/Sp.Attack) and Speed
                over defensive stats, as counters need to deal damage quickly and efficiently.
              </Typography>
            </Box>
          </Box>
        </Modal>
      </Container>
    </>
  );
}
